import { useRef, useEffect, useState, useCallback } from 'react';
import { useRackEditorStore, RackRow } from '../stores/rackEditorStore';

interface Point {
  x: number;
  y: number;
}

function RackEditor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<{ id: string; count: number } | null>(null);

  const {
    zones,
    selectedZone,
    rackRows,
    selectedRackRow,
    isOpen,
    isLoading,
    error,
    drawing,
    currentDraft,
    backgroundImage,
    imageScale: _imageScale,
    imageOffsetX: _imageOffsetX,
    imageOffsetY: _imageOffsetY,
    closeEditor,
    selectZone,
    selectRackRow,
    createRackRow,
    updateRackRow: _updateRackRow,
    deleteRackRow,
    generateLocations,
    startDrawing,
    updateDrawing,
    finishDrawing,
    cancelDrawing,
    updateDraft,
    resetDraft,
    setBackgroundImage,
    exportLocations,
  } = useRackEditorStore();

  // Resize handler
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height - 40 });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [isOpen]);

  // Canvas drawing
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = '#2a2a3e';
    ctx.lineWidth = 1;
    const gridSize = 50;
    for (let x = 0; x < canvas.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    // Draw existing rack rows
    rackRows.forEach((row) => {
      if (row.local_start_x !== undefined && row.local_end_x !== undefined) {
        const isSelected = selectedRackRow?.id === row.id;
        drawRackRow(ctx, row, isSelected);
      }
    });

    // Draw current drawing
    if (drawing.startPoint && drawing.endPoint) {
      ctx.strokeStyle = '#27ae60';
      ctx.lineWidth = 3;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(drawing.startPoint.x, drawing.startPoint.y);
      ctx.lineTo(drawing.endPoint.x, drawing.endPoint.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw rack preview
      const _length = Math.sqrt(
        Math.pow(drawing.endPoint.x - drawing.startPoint.x, 2) +
        Math.pow(drawing.endPoint.y - drawing.startPoint.y, 2)
      );
      const _angle = Math.atan2(
        drawing.endPoint.y - drawing.startPoint.y,
        drawing.endPoint.x - drawing.startPoint.x
      );
      void _length; void _angle; // Suppress unused warnings

      // Draw bay markers
      const bayCount = currentDraft.num_bays;
      ctx.fillStyle = 'rgba(39, 174, 96, 0.3)';
      for (let i = 0; i < bayCount; i++) {
        const t = (i + 0.5) / bayCount;
        const x = drawing.startPoint.x + t * (drawing.endPoint.x - drawing.startPoint.x);
        const y = drawing.startPoint.y + t * (drawing.endPoint.y - drawing.startPoint.y);
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }, [rackRows, selectedRackRow, drawing, currentDraft]);

  const drawRackRow = (ctx: CanvasRenderingContext2D, row: RackRow, isSelected: boolean) => {
    if (row.local_start_x === undefined || row.local_end_x === undefined) return;

    const startX = row.local_start_x;
    const startY = row.local_start_y || 0;
    const endX = row.local_end_x;
    const endY = row.local_end_y || 0;

    // Draw main line
    ctx.strokeStyle = isSelected ? '#3498db' : '#9b59b6';
    ctx.lineWidth = isSelected ? 4 : 2;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // Draw rack depth indicator
    const angle = Math.atan2(endY - startY, endX - startX);
    const perpAngle = angle + Math.PI / 2;
    const depthPx = 20; // Visual depth

    ctx.fillStyle = isSelected ? 'rgba(52, 152, 219, 0.2)' : 'rgba(155, 89, 182, 0.2)';
    ctx.beginPath();
    if (row.has_left_side) {
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.lineTo(endX + Math.cos(perpAngle) * depthPx, endY + Math.sin(perpAngle) * depthPx);
      ctx.lineTo(startX + Math.cos(perpAngle) * depthPx, startY + Math.sin(perpAngle) * depthPx);
      ctx.closePath();
      ctx.fill();
    }
    if (row.has_right_side) {
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.lineTo(endX - Math.cos(perpAngle) * depthPx, endY - Math.sin(perpAngle) * depthPx);
      ctx.lineTo(startX - Math.cos(perpAngle) * depthPx, startY - Math.sin(perpAngle) * depthPx);
      ctx.closePath();
      ctx.fill();
    }

    // Draw label
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    ctx.fillStyle = isSelected ? '#3498db' : '#fff';
    ctx.font = '12px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`A${row.aisle_number}`, midX, midY - 10);
  };

  useEffect(() => {
    draw();
  }, [draw]);

  // Mouse handlers
  const getCanvasPoint = (e: React.MouseEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!selectedZone) return;
    const point = getCanvasPoint(e);
    startDrawing(point.x, point.y);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drawing.isDrawing) return;
    const point = getCanvasPoint(e);
    updateDrawing(point.x, point.y);
  };

  const handleMouseUp = () => {
    if (!drawing.isDrawing) return;
    finishDrawing();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      cancelDrawing();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCreateRackRow = async () => {
    await createRackRow();
  };

  const handleGenerateLocations = async (id: string) => {
    setGeneratingId(id);
    try {
      const result = await generateLocations(id);
      setGenerationResult({ id, count: result.locations_created });
      setTimeout(() => setGenerationResult(null), 3000);
    } catch (err) {
      // Error handled by store
    } finally {
      setGeneratingId(null);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setBackgroundImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rack-editor-modal">
      <div className="rack-editor-container">
        <div className="rack-editor-header">
          <h2>Rack Placement Editor</h2>
          <button className="close-button" onClick={closeEditor}>&times;</button>
        </div>

        <div className="rack-editor-body">
          {/* Left Panel: Zone selection and rack list */}
          <div className="rack-editor-sidebar">
            <div className="sidebar-section">
              <h3>Zone</h3>
              <select
                className="zone-select"
                value={selectedZone?.id || ''}
                onChange={(e) => {
                  const zone = zones.find((z) => z.id === e.target.value);
                  if (zone) selectZone(zone);
                }}
              >
                <option value="">Select a zone...</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.code} - {zone.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedZone && (
              <>
                <div className="sidebar-section">
                  <h3>Rack Rows ({rackRows.length})</h3>
                  <div className="rack-row-list">
                    {rackRows.map((row) => (
                      <div
                        key={row.id}
                        className={`rack-row-item ${selectedRackRow?.id === row.id ? 'selected' : ''}`}
                        onClick={() => selectRackRow(row)}
                      >
                        <div className="rack-row-info">
                          <span className="rack-row-aisle">Aisle {row.aisle_number}</span>
                          <span className="rack-row-config">
                            {row.num_bays} bays x {row.num_levels} levels
                          </span>
                        </div>
                        <div className="rack-row-locations">
                          {row.location_count} loc
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3>New Rack Row</h3>
                  <div className="draft-form">
                    <div className="form-row">
                      <label>Aisle #</label>
                      <input
                        type="number"
                        min="1"
                        value={currentDraft.aisle_number}
                        onChange={(e) => updateDraft({ aisle_number: parseInt(e.target.value) || 1 })}
                      />
                    </div>
                    <div className="form-row">
                      <label>Bays</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={currentDraft.num_bays}
                        onChange={(e) => updateDraft({ num_bays: parseInt(e.target.value) || 10 })}
                      />
                    </div>
                    <div className="form-row">
                      <label>Levels</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={currentDraft.num_levels}
                        onChange={(e) => updateDraft({ num_levels: parseInt(e.target.value) || 4 })}
                      />
                    </div>
                    <div className="form-row">
                      <label>Prefix</label>
                      <input
                        type="text"
                        maxLength={10}
                        value={currentDraft.location_prefix}
                        onChange={(e) => updateDraft({ location_prefix: e.target.value })}
                      />
                    </div>
                    <div className="form-row">
                      <label>Levels</label>
                      <select
                        value={currentDraft.level_naming}
                        onChange={(e) => updateDraft({ level_naming: e.target.value as 'alpha' | 'numeric' })}
                      >
                        <option value="alpha">A, B, C...</option>
                        <option value="numeric">1, 2, 3...</option>
                      </select>
                    </div>
                    <div className="form-row checkboxes">
                      <label>
                        <input
                          type="checkbox"
                          checked={currentDraft.has_left_side}
                          onChange={(e) => updateDraft({ has_left_side: e.target.checked })}
                        />
                        Left Side
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={currentDraft.has_right_side}
                          onChange={(e) => updateDraft({ has_right_side: e.target.checked })}
                        />
                        Right Side
                      </label>
                    </div>
                  </div>
                  <p className="draw-hint">Draw on canvas to place rack row</p>
                  <button
                    className="create-button"
                    onClick={handleCreateRackRow}
                    disabled={!drawing.startPoint || !drawing.endPoint || isLoading}
                  >
                    {isLoading ? 'Creating...' : 'Create Rack Row'}
                  </button>
                  <button className="reset-button" onClick={resetDraft}>
                    Reset
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Center: Canvas */}
          <div className="rack-editor-canvas-container" ref={containerRef}>
            <div className="canvas-toolbar">
              <label className="image-upload-label">
                <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
                Load Floor Plan
              </label>
              {backgroundImage && (
                <button onClick={() => setBackgroundImage(null)}>Clear Image</button>
              )}
            </div>
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: selectedZone ? 'crosshair' : 'not-allowed' }}
            />
            {!selectedZone && (
              <div className="canvas-overlay">
                <p>Select a zone to start placing racks</p>
              </div>
            )}
          </div>

          {/* Right Panel: Selected rack row details */}
          <div className="rack-editor-details">
            {selectedRackRow ? (
              <>
                <h3>Rack Row Details</h3>
                <div className="detail-section">
                  <div className="detail-row">
                    <span>Aisle:</span>
                    <span>{selectedRackRow.aisle_number}</span>
                  </div>
                  <div className="detail-row">
                    <span>Configuration:</span>
                    <span>{selectedRackRow.num_bays} x {selectedRackRow.num_levels}</span>
                  </div>
                  <div className="detail-row">
                    <span>Prefix:</span>
                    <span>{selectedRackRow.location_prefix || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <span>Sides:</span>
                    <span>
                      {selectedRackRow.has_left_side && selectedRackRow.has_right_side
                        ? 'Both'
                        : selectedRackRow.has_left_side
                        ? 'Left only'
                        : 'Right only'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Locations:</span>
                    <span>{selectedRackRow.location_count}</span>
                  </div>
                </div>

                <div className="action-buttons">
                  <button
                    className="generate-button"
                    onClick={() => handleGenerateLocations(selectedRackRow.id)}
                    disabled={generatingId === selectedRackRow.id}
                  >
                    {generatingId === selectedRackRow.id ? 'Generating...' : 'Generate Locations'}
                  </button>
                  {generationResult?.id === selectedRackRow.id && (
                    <div className="generation-result">
                      Created {generationResult.count} locations
                    </div>
                  )}
                  <button
                    className="delete-button"
                    onClick={() => {
                      if (confirm('Delete this rack row?')) {
                        deleteRackRow(selectedRackRow.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>

                <h3>Location Code Preview</h3>
                <div className="location-preview">
                  <code>
                    {selectedRackRow.location_prefix}
                    {selectedRackRow.aisle_number}01A
                    {selectedRackRow.has_left_side && selectedRackRow.has_right_side ? '-L' : ''}
                  </code>
                  <span className="preview-label">First location</span>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>Select a rack row to view details</p>
              </div>
            )}

            {selectedZone && rackRows.length > 0 && (
              <div className="export-section">
                <h3>Export Locations</h3>
                <div className="export-buttons">
                  <button onClick={() => exportLocations('csv')}>
                    Export CSV
                  </button>
                  <button onClick={() => exportLocations('json')}>
                    Export JSON
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}
      </div>
    </div>
  );
}

export default RackEditor;
